import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import type { FormStep, Route } from "../contracts";

/*
 * The form route's steps live in the URL (`?step=fields&field=<id>&rule=<id>`), so browser
 * Back and Forward move between steps and Fix links deep-link to a field or a rule.
 */

export const FORM_STEPS: readonly FormStep[] = ["pages", "fields", "rules", "preview"];
export const CLINIC_RULES_STEPS: readonly FormStep[] = ["rules", "preview"];

export interface FormSetupSearch {
  readonly step?: FormStep;
  readonly field?: string;
  readonly rule?: string;
}
export interface ClinicRulesSearch {
  readonly step?: "rules" | "preview";
}

const isStep = (value: unknown): value is FormStep =>
  typeof value === "string" && (FORM_STEPS as readonly string[]).includes(value);

/** Field and rule ids are opaque keys of at most 160 characters (the contracts' KeySchema). */
const isKey = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length > 0 &&
  value.length <= 160 &&
  value.trim() === value &&
  !/\p{Cc}/u.test(value);

/** `/admin/forms/$formId` search: unknown steps and malformed ids are dropped. */
export function validateFormSetupSearch(search: Record<string, unknown>): FormSetupSearch {
  return {
    ...(isStep(search.step) ? { step: search.step } : {}),
    ...(isKey(search.field) ? { field: search.field } : {}),
    ...(isKey(search.rule) ? { rule: search.rule } : {}),
  };
}

/** `/admin/forms/instructions` (Clinic rules) search: its two steps only. */
export function validateClinicRulesSearch(search: Record<string, unknown>): ClinicRulesSearch {
  return search.step === "rules" || search.step === "preview" ? { step: search.step } : {};
}

/** The search a route writes: a step change drops a field or rule it does not name. */
export function routeSearch(route: Route): FormSetupSearch {
  return {
    step: route.step,
    ...(route.fieldId ? { field: route.fieldId } : {}),
    ...(route.ruleId ? { rule: route.ruleId } : {}),
  };
}

export interface FormStepLocation {
  /** The step in the URL, or undefined when the page should resume. */
  readonly step: FormStep | undefined;
  readonly fieldId: string | undefined;
  readonly ruleId: string | undefined;
  /**
   * Moves to a step (and optionally a field or rule). Steps the admin picks push history;
   * automatic moves pass `{ replace: true }`.
   */
  readonly goTo: (route: Route, opts?: { replace?: boolean }) => void;
}

/**
 * Reads and writes the step search params. It reads with `strict: false`, so it works on the
 * form route, the Clinic rules route and inside test routers.
 */
export function useFormStep(): FormStepLocation {
  const raw = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const { step, field, rule } = validateFormSetupSearch(raw);
  const goTo = useCallback(
    (route: Route, opts?: { replace?: boolean }) => {
      void navigate({
        to: ".",
        search: routeSearch(route) as never,
        replace: opts?.replace ?? false,
      });
    },
    [navigate],
  );
  return useMemo(() => ({ step, fieldId: field, ruleId: rule, goTo }), [step, field, rule, goTo]);
}
